import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, Form, Input, TimePicker } from "antd";
import dayjs from "dayjs";
import FormItem from "components/fields/FormItem";
import CrudButtons from "components/button/crudbuttons";
import { TIME_FORMAT_12HM } from "variables/constants";
import {
	getTimecardUpdateRequestDetails,
	doTimecardUpdateRequestCreate,
	doTimecardUpdateRequestUpdate,
	resetTimecardUpdateRequestState
} from "../../../../../redux/timecardupdaterequest/timecardUpdateRequestState";
import { requiredField } from "utils/crudSagaHelper";
import { CalendarRange } from "lucide-react";
import { AiOutlineSwapRight } from "react-icons/ai";


const TimecardUpdateRequestForm = ({
	resetState,
	mode = "edit",
	onCancel,
	timecard
}) => {

	const dispatch = useDispatch();
	const createSuccess = useSelector(
		(state) => state.timecardUpdateRequest.timecardUpdateRequestDetails.success
	);

	const [form] = Form.useForm();
	const { RangePicker } = DatePicker;

	const updatedSchedule = Form.useWatch("updated_schedule", form);
	const updatedPunchIn = Form.useWatch("updated_punch_in", form);
	const updatedPunchOut = Form.useWatch("updated_punch_out", form);
	const changeValidator = {
		validator: () => {
			if (hasChanges) {
				return Promise.resolve();
			}

			return Promise.reject(
				new Error("Please make at least one change")
			);
		},
	};

	const hasChanges = React.useMemo(() => {
		const originalScheduleIn = timecard?.tmc_schedule_time_in
			? dayjs(timecard.tmc_schedule_time_in).format("HH:mm:ss")
			: null;

		const originalScheduleOut = timecard?.tmc_schedule_time_out
			? dayjs(timecard.tmc_schedule_time_out).format("HH:mm:ss")
			: null;

		const originalPunchIn = timecard?.tmc_punch_in
			? dayjs(timecard.tmc_punch_in).format("HH:mm:ss")
			: null;

		const originalPunchOut = timecard?.tmc_punch_out
			? dayjs(timecard.tmc_punch_out).format("HH:mm:ss")
			: null;

		const scheduleChanged =
			(updatedSchedule?.[0]?.format("HH:mm:ss") ?? null) !== originalScheduleIn ||
			(updatedSchedule?.[1]?.format("HH:mm:ss") ?? null) !== originalScheduleOut;

		const punchChanged =
			(updatedPunchIn?.format("HH:mm:ss") ?? null) !== originalPunchIn ||
			(updatedPunchOut?.format("HH:mm:ss") ?? null) !== originalPunchOut;

		return scheduleChanged || punchChanged;
	}, [updatedSchedule, updatedPunchIn,updatedPunchOut, timecard]);
	
	useEffect(() => {
		if (createSuccess) {
			dispatch(resetTimecardUpdateRequestState());
			onCancel?.();
		}
	}, [createSuccess, onCancel, resetState]);

	useEffect(() => {
		if (!timecard) return;

		form.resetFields();

		form.setFieldsValue({
			tur_timecard_id: timecard.tmc_id,
			current_schedule: timecard.tmc_schedule_time_in
				? `${dayjs(timecard.tmc_schedule_time_in).format(TIME_FORMAT_12HM)} - ${dayjs(timecard.tmc_schedule_time_out).format(TIME_FORMAT_12HM)}`
				: "Rest Day",
			current_punch: `${timecard.tmc_punch_in
				? dayjs(timecard.tmc_punch_in).format(TIME_FORMAT_12HM)
				: "-"} - ${
				timecard.tmc_punch_out
					? dayjs(timecard.tmc_punch_out).format(TIME_FORMAT_12HM)
					: "-"
			}`,
			updated_schedule: timecard.tmc_schedule_time_in
				? [ dayjs(timecard.tmc_schedule_time_in),
					dayjs(timecard.tmc_schedule_time_out),]
				: null,

			updated_punch_in: timecard.tmc_punch_in
				? dayjs(timecard.tmc_punch_in)
				: null,
			updated_punch_out: timecard.tmc_punch_out
				? dayjs(timecard.tmc_punch_out)
				: null,
		});
	}, [timecard, form]);

	const onSubmit = (formData) => {
		const date = dayjs(timecard.tmc_date).format("YYYY-MM-DD");

		formData.tur_requested_schedule_time_in =
			formData.updated_schedule?.[0]
				? `${date} ${formData.updated_schedule[0].format("HH:mm:ss")}`
				: null;

		formData.tur_requested_schedule_time_out =
			formData.updated_schedule?.[1]
				? `${date} ${formData.updated_schedule[1].format("HH:mm:ss")}`
				: null;
				
		formData.tur_requested_punch_in =
			formData.updated_punch_in
				? `${date} ${formData.updated_punch_in.format("HH:mm:ss")}`
				: null;

		formData.tur_requested_punch_out =
			formData.updated_punch_out
				? `${date} ${formData.updated_punch_out.format("HH:mm:ss")}`
				: null;

		delete formData.updated_schedule;
		delete formData.updated_punch;

		dispatch(doTimecardUpdateRequestCreate(formData));
	};

	return (
		<Form
			form={form}
			layout="vertical"
			className="p-4 bg-white rounded-md dark:bg-intsamis-900"
			onFinish={onSubmit}
		>
			<FormItem name="tur_id" hidden>
				<Input />
			</FormItem>

			<FormItem name="tur_timecard_id" hidden>
				<Input />
			</FormItem>

			<div className="grid grid-cols-2 gap-4">
				<FormItem label="Schedule" name="current_schedule">
					<Input readOnly className="input-field" />
				</FormItem>

				<FormItem label="Punch In / Punch Out" name="current_punch">
					<Input readOnly className="input-field" />
				</FormItem>

				<FormItem 
					label="Update Schedule" 
					name="updated_schedule"
					dependencies={["updated_punch_in", "updated_punch_out"]}
					rules={[changeValidator]}
				>
					<RangePicker
						picker="time"
						format={TIME_FORMAT_12HM}
						className="input-field"
						style={{ width: "100%" }}
						allowClear={false}
					/>
				</FormItem>
				
				<FormItem
					label="Update Punch In / Punch Out"
					name="change_validator"
					dependencies={[
						"updated_schedule",
						"updated_punch_in",
						"updated_punch_out",
					]}
					rules={[changeValidator]}
					
				>
					<div className="flex gap-2 items-center">
						<FormItem
								name="updated_punch_in"
								noStyle
							>
								<TimePicker
									format={TIME_FORMAT_12HM}
									className="input-field"
								/>
							</FormItem>
							<span className="text-base text-gray-500"><AiOutlineSwapRight/></span>
							<FormItem
								name="updated_punch_out"
								noStyle
							>
								<TimePicker
									format={TIME_FORMAT_12HM}
									className="input-field"
								/>
						</FormItem>
					</div>
				</FormItem>
			</div>

			<FormItem 
				label="Reason" 
				name="tur_reason"
				rules={[requiredField]}
			>
				<Input.TextArea
					rows={4}
					placeholder="Enter reason for update request"
				/>
			</FormItem>

			<CrudButtons
				submitDisabled={!hasChanges}
				isModal
				cancelLabel="Cancel"
				doHideModal={onCancel}
				submitLabel="Submit"
				moduleId={0}
			/>
		</Form>
	);
};


export default (TimecardUpdateRequestForm);